import { useRef } from "react";
import { ReactComponent as InfoIcon } from "../assets/icons/info.svg";
import { ReactComponent as LeftChevronIcon } from "../assets/icons/left-arrow.svg";
import { Tooltip } from "./tooltip";

export function Panel({
  actions,
  children,
  label,
  className = "",
  contentClassName = "",
  showHeader = true,
  showNavigation = false,
  id,
  style,
  infoTooltip,
  hasBorder = false,
  headerClassName = "",
  labelExtra,
  footer,
}: {
  actions?: React.ReactNode;
  children: React.ReactNode;
  label: string | React.ReactNode;
  className?: string;
  contentClassName?: string;
  showHeader?: boolean;
  showNavigation?: boolean;
  id?: string;
  style?: React.CSSProperties;
  infoTooltip?: string;
  hasBorder?: boolean;
  headerClassName?: string;
  labelExtra?: React.ReactNode;
  /**
   * Optional content rendered below the panel body, inside the card chrome.
   * Used by marketing-dashboard for the bottom-anchored insight pill (same
   * `.d-insight` pattern as the main dashboard chart pills).
   */
  footer?: React.ReactNode;
}) {
  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <div
      id={id}
      className={`relative w-full h-full min-h-0 flex flex-col overflow-hidden bg-surface-primary p-2 sm:p-3 rounded-lg shadow-soft border border-border-primary [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${className}`}
      onClick={(e) => e.stopPropagation()}
      style={{ ...style }}
    >
      {showHeader && (
        <div
          className={`header min-h-[48px] px-3 sm:px-4 py-1 flex justify-between items-center gap-2 sm:gap-0 flex-shrink-0 ${headerClassName}`}
        >
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {typeof label === "string" ? (
              // UI-kit aligned: title uses .d-card-title (Geist 16px 600,
              // letter-spacing -0.01em) so the panel header matches the home
              // dashboard chart cards. Callers that need plain bold text can
              // still pass a custom node via the `label` prop.
              <h3 className="d-card-title text-left whitespace-nowrap mr-1">
                {label}
              </h3>
            ) : (
              label
            )}
            {infoTooltip && (
              <>
                <span
                  className="d-card-info cursor-pointer z-10"
                  data-tooltip-id={`tooltip-${id || "panel"}`}
                >
                  <InfoIcon className="w-4 h-4" />
                </span>
                <Tooltip
                  id={`tooltip-${id || "panel"}`}
                  showIcon={false}
                  isHtml={true}
                >
                  {infoTooltip}
                </Tooltip>
              </>
            )}
            {labelExtra}
          </div>
          {actions && (
            <div className="w-full sm:w-auto flex justify-end">{actions}</div>
          )}
        </div>
      )}
      {hasBorder && <div className=" border-t border-border-primary"></div>}
      <div
        ref={tableRef}
        className={`flex-1 min-h-0 overflow-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${contentClassName}`}
      >
        {children}

        {showNavigation && (
          <div
            className={`flex md:hidden absolute bottom-3 left-0 right-0 items-center gap-2 justify-center`}
          >
            <button
              type="button"
              onClick={() => tableRef.current?.scrollBy(-300, 0)}
              title={"left"}
              className="w-6 h-6 shadow-card flex justify-center items-center rounded border border-border-primary bg-gray-50"
            >
              <LeftChevronIcon className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => tableRef.current?.scrollBy(300, 0)}
              title={"right"}
              className="w-6 h-6 shadow-card flex justify-center items-center rounded border border-border-primary bg-gray-50"
            >
              <LeftChevronIcon className="w-4 h-4 rotate-180" />
            </button>
          </div>
        )}
      </div>
      {footer ? <div className="flex-shrink-0">{footer}</div> : null}
    </div>
  );
}
